import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'

import { Text } from '@mxenabled/mxui'
import { Cancel as CancelIcon } from '@mxenabled/mx-icons'

import { useEvent, useGlobalCopyStore } from '../../../common'
import type { Transaction } from '../../../core'
import { useAriaLive } from '../../../common/hooks/useAriaLive'

import SplitRow from './SplitRow'

import { ON_CLICK_EVENTS } from '../../constants/OnClickEvents'
import { formatCurrency } from '../../../core/utils/NumberFormatting'
import { buildDynamicCopy } from '../../../core/utils/Localization'

interface SplitManagerProps {
  onAddSplit: () => void
  onCategoryChanged: (index: number, categoryGuid: string) => void
  onDeleteSplit: (index: number) => void
  onUpdateAmount: (index: number, amount: number) => void
  splits: Transaction[]
  transaction: Transaction
}

const SplitManager: React.FC<SplitManagerProps> = ({
  onAddSplit,
  onCategoryChanged,
  onDeleteSplit,
  onUpdateAmount,
  splits,
  transaction,
}) => {
  const { transactions: copy } = useGlobalCopyStore()
  const { onEvent } = useEvent()

  const [error, setError] = React.useState('')
  const { announce, ariaLive } = useAriaLive()

  React.useEffect(() => {
    if (splits.length <= 1) {
      setError(copy.split_transaction_errors_min_splits)
      announce(copy.split_transaction_errors_min_splits)
    } else if (splits[0].amount < 0) {
      const errorCopy = buildDynamicCopy(
        copy.split_transaction_errors_totals_must_equal_amount,
        formatCurrency(transaction.amount, '0,0.00'),
      ) as string
      setError(errorCopy)
      announce(errorCopy)
    } else {
      setError('')
      announce('')
    }
  }, [splits])

  return (
    <Box className="mx-txn-split-manager">
      {ariaLive}
      {splits.map((split, index) => (
        <SplitRow
          index={index}
          key={index}
          onCategoryChanged={onCategoryChanged}
          onDeleteRow={onDeleteSplit}
          onUpdateAmount={onUpdateAmount}
          split={split}
        />
      ))}
      {error && (
        <Stack bgcolor="background.paper" color="error.main" direction="row" gap={8} p={16}>
          {/* TODO: Move span with aria-label and role into @mxenabled/mx-icons */}
          <span aria-label="error" role="img">
            <CancelIcon sx={{ fontSize: 15 }} />
          </span>
          <Text variant="body2">{error}</Text>
        </Stack>
      )}
      <Box p={16}>
        <Button
          aria-label={copy.split_transaction_add_split_btn}
          onClick={() => {
            onEvent(ON_CLICK_EVENTS.ON_TRANSACTION_SPLIT_ADD_CLICK)
            onAddSplit()
          }}
        >
          {copy.split_transaction_add_split_btn}
        </Button>
      </Box>
    </Box>
  )
}

export default observer(SplitManager)
